/**
 * The single hostname allowlist gating every QA-only affordance in this
 * app - campaign `?qa=` modes (`QaMode.ts`) and the Daily Challenge QA
 * panel/date override (`dailyQa.ts`, and the `daily-challenge` Netlify
 * Function's server-side re-check) all resolve through this one file.
 *
 * Deliberately zero imports of its own (not even the `@/*` path alias) -
 * `netlify/functions/daily-challenge.ts` imports this by relative path to
 * re-validate the QA date override server-side, and Netlify's Function
 * bundler does not resolve Vite's `@/*` alias, so anything reachable from
 * a Function must only ever pull in plain relative imports, all the way
 * down. Keeping this predicate in its own leaf module (rather than
 * inline in `QaMode.ts`, which itself imports `@/config/GameConfig` etc.
 * for unrelated exports) is what makes that safe.
 */

/**
 * Matches only this project's Netlify Deploy Preview hostnames, e.g.
 * `deploy-preview-6--snake-arenaz.netlify.app` - never the production
 * hostname (`snake-arenaz.netlify.app` itself, with no
 * `deploy-preview-N--` prefix), never a branch-deploy hostname, never
 * `snakescore.com`/`www.snakescore.com`, and never any other
 * `*.netlify.app` site. Anchored on both ends so nothing can smuggle a
 * valid prefix/suffix around an arbitrary host.
 */
const DEPLOY_PREVIEW_HOSTNAME_PATTERN = /^deploy-preview-\d+--snake-arenaz\.netlify\.app$/;

/** Local development hosts only - never `0.0.0.0` (a bind address, not something a browser/request is ever actually served from) and never a bare LAN IP, which could be reachable by more than just the developer. */
const LOCAL_DEV_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

/**
 * Strict allowlist: true only for local dev hosts or this exact
 * project's Deploy Preview hostname pattern. Everything else - the real
 * production hostname, any future custom domain, any other Netlify
 * project/site - is denied. Deliberately an allowlist, never a
 * denylist/exclusion of "the known production hostname", so a future
 * production or custom domain is protected by default without needing
 * to be remembered here.
 */
export function isQaModeAllowedHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const host = hostname.toLowerCase();
  return LOCAL_DEV_HOSTNAMES.has(host) || DEPLOY_PREVIEW_HOSTNAME_PATTERN.test(host);
}
